import React, { useCallback } from 'react';
import {
  Button,
  ChevronRightIcon,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  Money,
  StatusDot,
  statusToken,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { formatCurrency } from '../../../shared/utils/currency';
import { maskAccountNumber, maskSortCode } from '../utils/bankDetailMasks';
import type { PaymentRowDto } from '../api/paymentsApi';

export interface PaymentListItemProps {
  payment: PaymentRowDto;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  showBankDetails: boolean;
  onClick?: () => void;
  'data-testid'?: string;
}

export const PaymentListItem = React.memo(function PaymentListItem({
  payment,
  columns,
  collapse,
  showBankDetails,
  onClick,
  'data-testid': dataTestId,
}: PaymentListItemProps) {
  const handleClick = useCallback(() => onClick?.(), [onClick]);
  const account = showBankDetails
    ? payment.accountNumber || 'Not provided'
    : maskAccountNumber(payment.accountNumber);
  const sortCode = showBankDetails
    ? payment.sortCode || 'Not provided'
    : maskSortCode(payment.sortCode);
  const amount = formatCurrency(payment.amountDue);

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      lead={<StatusDot color={statusToken(payment.potStatus).color} />}
      title={payment.personName}
      cells={{
        status: payment.potStatus,
        account,
        sortCode,
      }}
      meta={`${payment.potStatus} · ${account} · ${sortCode}`}
      value={<Money value={payment.amountDue} />}
      hoverCard={
        <LedgerHoverCard
          title={payment.personName}
          kind="Payment"
          onOpenDetails={handleClick}
          openDetailsLabel="Open payment details"
        >
          <Fact label="Company">{payment.companyName || 'Not provided'}</Fact>
          <Fact label="Status">{payment.potStatus}</Fact>
          <Fact label="Pot reference">{payment.potReference}</Fact>
          <Fact label="Account">{account}</Fact>
          <Fact label="Sort code">{sortCode}</Fact>
          <Fact label="Amount">{amount}</Fact>
        </LedgerHoverCard>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          aria-label={`Open ${payment.personName} payment`}
          className="size-8"
        >
          <ChevronRightIcon aria-hidden="true" className="size-4" />
        </Button>
      }
      onClick={handleClick}
      data-testid={dataTestId}
    />
  );
});
